import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatCurrency, formatDateLong } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { ShieldCheck, MessageSquareWarning, FileText, Clock, User, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function ApprovalQueuePage() {
  const [data, setData] = useState({ pending_quotes: [], pending_pos: [] });
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState("");
  const [changesDialog, setChangesDialog] = useState(null);
  const [changesNotes, setChangesNotes] = useState("");
  const navigate = useNavigate();
  const { user } = useAuth();

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/approval-queue");
      setData(res.data);
    } catch { toast.error("Failed to load approval queue"); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchQueue(); }, [fetchQueue]);

  const handleApprove = async (quoteId, quoteNumber) => {
    setActionLoading(quoteId);
    try {
      await api.post(`/quotes/${quoteId}/approve`, { notes: "" });
      toast.success(`${quoteNumber} approved`);
      fetchQueue();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to approve"); }
    setActionLoading("");
  };

  const handleRequestChanges = async () => {
    if (!changesNotes.trim()) { toast.error("Please provide notes"); return; }
    setActionLoading(changesDialog.quote_id);
    try {
      await api.post(`/quotes/${changesDialog.quote_id}/request-changes`, { notes: changesNotes });
      toast.success(`Changes requested for ${changesDialog.quote_number}`);
      setChangesDialog(null);
      setChangesNotes("");
      fetchQueue();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setActionLoading("");
  };

  const totalPending = data.pending_quotes.length + data.pending_pos.length;

  if (loading) return (
    <div className="flex items-center justify-center min-h-[400px]">
      <Loader2 className="w-6 h-6 animate-spin text-[#E63946]" />
    </div>
  );

  return (
    <div data-testid="approval-queue-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#1F2937]">Approval Queue</h1>
          <p className="text-sm text-gray-500 mt-1">
            {totalPending === 0 ? "All caught up — nothing pending." : `${totalPending} item${totalPending > 1 ? "s" : ""} awaiting your review`}
          </p>
        </div>
        <Badge className="bg-amber-50 text-amber-700 border border-amber-200 text-sm px-3 py-1.5 rounded-sm">
          {totalPending} Pending
        </Badge>
      </div>

      {totalPending === 0 && (
        <Card className="border border-dashed border-gray-300 rounded-sm">
          <CardContent className="p-12 text-center">
            <ShieldCheck className="w-12 h-12 text-green-400 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">Nothing to approve</p>
            <p className="text-xs text-gray-400 mt-1">When Sales Reps submit quotes, they'll appear here for your review.</p>
          </CardContent>
        </Card>
      )}

      {data.pending_quotes.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-500 mb-3 flex items-center gap-2">
            <FileText className="w-4 h-4" /> Quotes Pending Approval ({data.pending_quotes.length})
          </h2>
          <div className="space-y-3">
            {data.pending_quotes.map((q) => (
              <Card key={q.quote_id} data-testid={`approval-item-${q.quote_id}`}
                className="border border-amber-200 rounded-sm shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/quotes/${q.quote_id}`)}>
                      <div className="flex items-center gap-3">
                        <span className="font-mono font-semibold text-[#E63946] text-sm">{q.quote_number}</span>
                        <span className="text-gray-400">•</span>
                        <span className="text-sm text-gray-700 truncate">{q.customer_name}</span>
                        <span className="text-gray-400">•</span>
                        <span className="font-semibold text-sm">{formatCurrency(q.total)}</span>
                      </div>
                      <div className="flex items-center gap-4 mt-1.5 text-xs text-gray-500">
                        <span className="flex items-center gap-1"><User className="w-3 h-3" /> {q.created_by_name}</span>
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {formatDateLong(q.created_at)}</span>
                        <span>{q.line_items?.length || 0} item{(q.line_items?.length || 0) !== 1 ? "s" : ""}</span>
                      </div>
                      {q.change_request_notes && (
                        <div className="mt-2 text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded-sm inline-block">
                          Previous feedback: {q.change_request_notes}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button data-testid={`approve-${q.quote_id}`} size="sm" onClick={() => handleApprove(q.quote_id, q.quote_number)}
                        disabled={actionLoading === q.quote_id}
                        className="bg-green-600 hover:bg-green-700 text-white rounded-sm h-8 px-3 text-xs">
                        {actionLoading === q.quote_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5 mr-1" />}
                        Approve
                      </Button>
                      <Button data-testid={`request-changes-${q.quote_id}`} variant="outline" size="sm"
                        onClick={() => { setChangesDialog(q); setChangesNotes(""); }}
                        className="rounded-sm h-8 px-3 text-xs text-amber-600 border-amber-200 hover:bg-amber-50">
                        <MessageSquareWarning className="w-3.5 h-3.5 mr-1" /> Changes
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      <AlertDialog open={!!changesDialog} onOpenChange={() => setChangesDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Request Changes — {changesDialog?.quote_number}</AlertDialogTitle>
            <AlertDialogDescription>
              <p className="text-sm text-gray-500 mb-2">Explain what changes are needed. The sales rep will be notified and can edit and resubmit.</p>
              <textarea data-testid="queue-changes-notes" value={changesNotes} onChange={(e) => setChangesNotes(e.target.value)}
                placeholder="Describe the changes needed..."
                className="w-full rounded-sm border border-gray-300 px-3 py-2 text-sm h-24 resize-none focus:outline-none focus:ring-2 focus:ring-amber-500" />
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="queue-confirm-changes" onClick={handleRequestChanges}
              className="bg-amber-500 hover:bg-amber-600 text-white rounded-sm">Send Request</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
